import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const authAxios = axios.create({ baseURL: API, withCredentials: true });

export function AuthShell({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#090A0E] px-4 text-slate-200">
      <div className="grain-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#12141C]/90 p-8 text-center shadow-[0_0_60px_rgba(0,0,0,0.6)] backdrop-blur-md sm:p-10"
      >
        <img src="/media/logo.png" alt="Elite Mobile Detail" className="mx-auto mb-8 h-14 w-auto" />
        {children}
      </motion.div>
    </div>
  );
}

export function Login() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    authAxios.get("/auth/me").then(() => navigate("/dashboard", { replace: true })).catch(() => setChecking(false));
  }, [navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703]">Owner Access</p>
      <h1 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Bookings Dashboard</h1>
      <p className="mt-4 text-sm leading-relaxed text-slate-400">Sign in with the Google account linked to Elite Mobile Detail to view quote requests and deposits.</p>
      <button
        type="button"
        onClick={signIn}
        disabled={checking}
        data-testid="google-signin-button"
        className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,255,255,0.25)] disabled:opacity-60"
      >
        {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
        Continue with Google
      </button>
      <Link to="/" data-testid="login-back-home" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to site
      </Link>
    </AuthShell>
  );
}

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", location.pathname);
    if (!sessionId) return navigate("/login", { replace: true });
    authAxios
      .post("/auth/session", { session_id: sessionId })
      .then((r) => navigate("/dashboard", { replace: true, state: { user: r.data } }))
      .catch(() => setError(true));
  }, [location, navigate]);

  return (
    <AuthShell>
      {error ? (
        <div data-testid="auth-callback-error">
          <p className="text-sm text-slate-400">Sign-in could not be completed.</p>
          <Link to="/login" className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703]">Try again</Link>
        </div>
      ) : (
        <Loader2 data-testid="auth-callback-loading" className="mx-auto h-8 w-8 animate-spin text-[#FFB703]" />
      )}
    </AuthShell>
  );
}
